import React, { useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import {
  addInvoiceLog,
  addChatUser,
  deleteChatUser,
  getChatUsers,
  getInvoiceFiles,
  updateEmailChatUpdates,
} from '../../../../redux/slices/invoicing';
import { useDispatch, useSelector } from '../../../../redux/store';
import ChatBox from '../../../../reusable-components/ChatBox/ChatBox';
import { getMessagesAndUserActionsSorted } from '../../../../reusable-components/ChatBox/chatHelper';

function extractMentionsFromHtml(htmlString = '') {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const mentionElements = doc.querySelectorAll('span.mention');
  return Array.from(mentionElements).map((mention) => ({
    name: mention.getAttribute('data-value'),
    id: mention.getAttribute('data-id'),
  }));
}

function normalizeMentionSpacing(htmlString = '') {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const mentionElements = doc.querySelectorAll('span.mention');
  mentionElements.forEach((span) => {
    if (!span.nextSibling || span.nextSibling.nodeValue?.trim() !== '') {
      span.parentNode.insertBefore(document.createTextNode(' '), span.nextSibling);
    }
  });

  return doc.body.innerHTML;
}

InvoiceChat.propTypes = {
  messages: PropTypes.array,
  selectedChatItems: PropTypes.array,
  addItemsToChat: PropTypes.bool,
  setAddItemsToChat: PropTypes.func,
};

export default function InvoiceChat({ messages, selectedChatItems, addItemsToChat, setAddItemsToChat }) {
  const dispatch = useDispatch();

  const { data: { invoiceChatContacts, invoiceChatUsers, emailChatUpdates } } = useSelector((state) => state.invoicing);
  const { currentChatUsers, allChatUsers } = invoiceChatUsers || {};

  const { id } = useParams();

  useEffect(() => {
    dispatch(getChatUsers(id));
  }, [dispatch, id]);

  const handleSend = async (comment, parentCommentId, files = []) => {
    const mentions = extractMentionsFromHtml(comment);
    const formattedComment = normalizeMentionSpacing(comment);

    const invoiceUrl = window.location.href;
    const result = await dispatch(
      addInvoiceLog({
        invoiceId: id,
        messageType: 'Comment',
        extraInfo: formattedComment,
        mentions,
        parentCommentId: parentCommentId || null,
        files,
        invoiceUrl,
      })
    );

    if (result !== 'success') return;

    await dispatch(getInvoiceFiles(id));
    dispatch(getChatUsers(id));
  };

  const addUser = (userId = null, userEmail = null) => {
    const invoiceUrl = window.location.href;
    dispatch(addChatUser(id, userId, invoiceUrl, userEmail));
  };

  const deleteUser = (userId = null, userEmail = null) => {
    dispatch(deleteChatUser(id, userId, userEmail));
  };

  const messagesAndUserActionsSorted = useMemo(() => {
    if (messages && allChatUsers) return getMessagesAndUserActionsSorted(messages, allChatUsers, 'chat');
    return [];
  }, [messages, allChatUsers]);

  const allowedUsers = useMemo(
    () =>
      invoiceChatContacts
        .filter((u) => !currentChatUsers?.some((c) => u.userId === c.id))
        .map((u) => ({ id: u.userId, email: u.id, value: u.value })),
    [invoiceChatContacts, currentChatUsers]
  );

  return (
    <Box
      sx={{
        padding: '15px',
        pt: 0,
        borderRadius: '4px',
        boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
        backgroundColor: '#fff',
        border: 'solid 1px #c1c9d0',
      }}
    >
      <ChatBox
        messagesAndUserActions={messagesAndUserActionsSorted}
        handleSend={handleSend}
        allowedUsers={allowedUsers}
        chatUsers={currentChatUsers}
        addUser={addUser}
        deleteUser={deleteUser}
        emailChatOn={emailChatUpdates}
        updateEmailChat={(sendUpdates) => dispatch(updateEmailChatUpdates(id, sendUpdates))}
        selectedChatItems={selectedChatItems}
        addItemsToChat={addItemsToChat}
        setAddItemsToChat={setAddItemsToChat}
      />
    </Box>
  );
}